
import moment from 'moment';
import _round from 'lodash/round';
import Config from 'services/config/local';
import { getHoursMinutesFormat, celsiusToFahrenheit, pascalToInchesOfWater, INCHES_OF_WATER_DECIMALS, isCelsiusField, isMobileLayout, alertTriggerDescription, ruleFields } from 'services/utils';
import { UPDATE_ALERT} from 'services/Permissions';
import { RANGE_LABEL_WEEK, RANGE_LABEL_3_YEARS, RANGE_LABEL_MONTH, SENSOR_GRAPH_RANGES, EXTENDED_SENSOR_GRAPH_RANGES, ALERT_EVENT_TYPES, TEMPERATURE_GRAPH_FILTERS } from 'services/charting/constants';
import { MEASUREMENT_SYSTEM_CHANGED_EVENT } from 'services/StudioEvents';
import {
    getBatteryStateLabel,
    getBatteryIcon,
    getBatteryStateBadgeClass,
    getBatteryTooltipSentence,
    getBatteryPercentage,
    getEstimatedRemainingYearsDisplay
} from 'services/BatteryHelper';
import { States } from '../../../../app.router';

import DisableAlertsModalController from '../../../../components/sensors-and-connectors/components/disable-alerts-modal/controller';
import DisableAlertsModalTemplate from '../../../../components/sensors-and-connectors/components/disable-alerts-modal/template.html';

const getAlertTagPresets = () => {
    const presets = Array.isArray(Config?.alerts?.tagPresets) && Config.alerts.tagPresets.length
        ? Config.alerts.tagPresets
        : [];
    return presets
        .map(tag => (tag || '').trim())
        .filter(tag => tag.length > 0)
        .filter((tag, index, self) => self.indexOf(tag) === index);
};


/* @ngInject */
export default class ActiveAlertsController {

    constructor($state, $scope, $timeout, AlertsAndRulesService, SensorService, FeatureFlags, UserPreferencesManager, DialogService, RoleManager, StorageService, AnalyticsService) {
        this.$state = $state;
        this.$scope = $scope;
        this.$timeout = $timeout;
        this.AlertsAndRulesService = AlertsAndRulesService;
        this.SensorService = SensorService;
        this.FeatureFlags = FeatureFlags;
        this.UserPreferencesManager = UserPreferencesManager;
        this.DialogService = DialogService;
        this.RoleManager = RoleManager;
        this.StorageService = StorageService;
        this.AnalyticsService = AnalyticsService;
        this.predefinedTagPresets = getAlertTagPresets();

        // Alert feedback state
        this.feedbackAwaitingReason = false;
        this.feedbackReasonText = '';
        this.feedbackJustSubmitted = false;
        this.feedbackIsHelpful = null;
    }

    get hasLongTermStorageFeatureFlag() {
        return this.FeatureFlags.isActive('sensor_long_term_storage')
    }

    get canPerformAlertActions() {
        return this.RoleManager.can(UPDATE_ALERT);
    }

    get availableRanges() {
        if (this.hasLongTermStorageFeatureFlag) {
            return SENSOR_GRAPH_RANGES;
        }

        return SENSOR_GRAPH_RANGES.filter(range => !EXTENDED_SENSOR_GRAPH_RANGES.includes(range));
    }

    get greatestRangeLabel() {
        return this.hasLongTermStorageFeatureFlag ? RANGE_LABEL_3_YEARS : RANGE_LABEL_MONTH;
    }

    get unavailableRanges() {
        if (this.hasLongTermStorageFeatureFlag) {
            return [];
        }

        return EXTENDED_SENSOR_GRAPH_RANGES;
    }

    get alertIcon() {
        let iconName = this.alert.trigger.field

        if (iconName === 'relativeHumidity') {
            iconName = 'humidity'
        }

        if (iconName === 'virtualGlycolBuffer') {
            iconName = 'temperature'
        }

        if (iconName === 'connectionStatus') {
            iconName = this.alert.device.typeIcon
        }

        if (iconName === 'objectPresent') {
            return 'proximity'
        }

        if (iconName === 'waterPresent') {
            return 'waterDetector'
        }

        if (iconName === 'batteryStatus') {
            return this.alert.trigger.batteryState === 'BATTERY_STATE_CRITICAL' ? 'battery_warning' : 'battery_low'
        }

        return iconName
    }

    getBatteryStateLabel(device) { return getBatteryStateLabel(device); } // eslint-disable-line class-methods-use-this

    getBatteryStateBadgeClass(device) { return getBatteryStateBadgeClass(device); } // eslint-disable-line class-methods-use-this

    getBatteryIcon(device) { return getBatteryIcon(device); } // eslint-disable-line class-methods-use-this

    getBatteryTooltipSentence(device) { return getBatteryTooltipSentence(device); } // eslint-disable-line class-methods-use-this

    getEstimatedRemainingYearsDisplay(device) { return getEstimatedRemainingYearsDisplay(device); } // eslint-disable-line class-methods-use-this

    getBatteryPercentage(device) { return getBatteryPercentage(device); } // eslint-disable-line class-methods-use-this

    get inputPlaceholderForCurrentUserAction() {
        if (this.currentUserAction === ALERT_EVENT_TYPES.ACKNOWLEDGED) {
            return 'Add a comment (optional)';
        }

        if (this.currentUserAction === ALERT_EVENT_TYPES.CORRECTIVE_ACTION) {
            return 'Add a corrective action';
        }

        if (this.currentUserAction === ALERT_EVENT_TYPES.COMMENT) {
            return 'Add a comment';
        }

        return '';
    }

    get lowerUpperThresholds() {

        let lowerRange = this.alert.trigger.range?.lower;
        let upperRange = this.alert.trigger.range?.upper;
        const isTemperatureLikeField = isCelsiusField(this.alert.trigger.field);

        if (lowerRange !== null && isTemperatureLikeField && this.UserPreferencesManager.useFahrenheit) {
            lowerRange = celsiusToFahrenheit(lowerRange).toFixed(1);
        }

        if (upperRange !== null && isTemperatureLikeField && this.UserPreferencesManager.useFahrenheit) {
            upperRange = celsiusToFahrenheit(upperRange).toFixed(1);
        }

        const isInchesOfWater = this.alert.trigger.field === 'differentialPressure' && this.UserPreferencesManager.useInchesOfWater;
        if (isInchesOfWater && lowerRange != null) {
            lowerRange = _round(pascalToInchesOfWater(lowerRange), INCHES_OF_WATER_DECIMALS);
        }
        if (isInchesOfWater && upperRange != null) {
            upperRange = _round(pascalToInchesOfWater(upperRange), INCHES_OF_WATER_DECIMALS);
        }

        return `${lowerRange},${upperRange}`
    }

    get timeToResolvedDuration() {
        const triggeredEvent = this.alert.events.find(event => event.type === ALERT_EVENT_TYPES.TRIGGERED);
        const resolvedEvent = this.alert.events.find(event => event.type === ALERT_EVENT_TYPES.RESOLVED);
        if (!triggeredEvent || !resolvedEvent) {
            return null;
        }

        const triggeredTime = moment(triggeredEvent.createTime);
        const resolvedTime = moment(resolvedEvent.createTime);
        return moment.duration(resolvedTime.diff(triggeredTime)).humanize();
    }

    get triggerEvent() {
        return this.alert.events.find(e => e.type === 'TRIGGERED') || null;
    }

    $onInit() {
        this.fields = ruleFields;
        this.ranges = SENSOR_GRAPH_RANGES
        this.currentUserAction = null
        this.currentUserActionText = ''
        this.showOptions = false
        this.graphVisible = true
        if (!Array.isArray(this.alert.tags)) {
            this.alert.tags = []
        }

        // Re-render the chart and rebuild the trigger description in the new unit (API-250, SOL-491).
        this.$scope.$on(MEASUREMENT_SYSTEM_CHANGED_EVENT, () => {
            this.updateTriggerDescriptions()
            this.graphVisible = false
            this.$timeout(() => {
                this.graphVisible = true
            })
        })

        // Inline tag picker state
        this.showTagPicker = false
        this.tagInput = ''
        this.tagSaving = false

        // Check if the trigger has filter applied. virtualGlycolBuffer takes precedence
        // over the legacy PET simulation, same as showPetGlycolFilter() in threshold-builder.
        if (this.alert.trigger.field === 'virtualGlycolBuffer' && this.FeatureFlags.isActive('sensor_virtual_glycol_buffer')) {
            this.filter = TEMPERATURE_GRAPH_FILTERS.VIRTUAL_GLYCOL_BUFFER
        } else if (this.alert.trigger.range?.filter?.productEquivalentTemperature) {
            this.filter = TEMPERATURE_GRAPH_FILTERS.GLYCOL
        } else {
            this.filter = null
        }

        this.isMobile = isMobileLayout()

        this.popoverEvent = null
        this.popoverHeader = ''
        this.popoverTitle = ''
        this.popoverSubject = ''
        this.popoverContent = ''

        this.convertEventsForTimeline()
        this.updateTriggerDescriptions()
    }

    // Contains the threshold in the user's temperature unit, so rebuilt on unit change.
    updateTriggerDescriptions() {
        this.alert.triggerDescription = alertTriggerDescription(this.alert);

        const deviceTriggerTime = this.alert.events.find(e => e.type === 'TRIGGERED').triggered.deviceTriggerTime
        let endTime = moment()
        if (this.alert.status === 'RESOLVED') {
            endTime = moment(this.alert.resolveTime)
        }
        if (this.alert.status === 'ARCHIVED') {
            endTime = moment(this.alert.archiveTime)
        }
        const formattedDeviceTriggerTime = this.getDuration(deviceTriggerTime, endTime);
        this.alert.deviceTriggerDescription = `${alertTriggerDescription(this.alert)} for ${formattedDeviceTriggerTime}`;
    }

    convertEventsForTimeline() {
        // Convert createTime to human readable format

        // Add an delayed event to the start of the events array
        const triggeredEvent = this.alert.events.find(e => e.type === 'TRIGGERED')
        const trigger = triggeredEvent.triggered
        if (trigger.triggerDelay !== null && this.alert.events.some(e => e.type === 'DELAY') === false) {
            // Anchor the delay to the TRIGGERED event, not to deviceTriggerTime: a rule
            // schedule can hold the alert back long after the device left the threshold,
            // and then deviceTriggerTime is nowhere near one triggerDelay before the alert.
            const delayStart = moment(triggeredEvent.createTime).subtract(parseInt(trigger.triggerDelay, 10), 'seconds')
            this.alert.events.push({
                createTime: delayStart.toISOString(),
                eventId: 'DELAY', // Need something here
                type: 'DELAY',
                createTimeFormatted: delayStart.format(`ddd, MMM D, ${getHoursMinutesFormat()}`),
                timeSince: delayStart.fromNow(),
            });
        }

        this.alert.events.forEach(event => {
            event.createTimeFormatted = moment(event.createTime).format(`ddd, MMM D, ${getHoursMinutesFormat()}`);
            event.timeSince = moment(event.createTime).fromNow();
            if (event.timeSince === 'a few seconds ago' || event.timeSince === 'in a few seconds') {
                event.timeSince = 'just now';
            }

            // Convert type to a more friendly format
            switch (event.type) {
                case ALERT_EVENT_TYPES.TRIGGERED:
                    event.typeFormatted = 'Open - Active Alert';
                    break;
                case ALERT_EVENT_TYPES.NOTIFICATION_DELIVERY:
                    event.typeFormatted = event.notificationDelivery.title;
                    break;
                case ALERT_EVENT_TYPES.ESCALATED:
                    event.typeFormatted = `Escalated to new recipients`;
                    break;
                case ALERT_EVENT_TYPES.ACKNOWLEDGED:
                    event.typeFormatted = `Acknowledged by ${event.acknowledged.account.displayName || event.acknowledged.account.email}`;
                    break;
                case ALERT_EVENT_TYPES.UNACKNOWLEDGED:
                    event.typeFormatted = 'Alert re-opened due to inactivity';
                    break;
                case ALERT_EVENT_TYPES.CORRECTIVE_ACTION:
                    event.typeFormatted = `Corrective Action by ${event.correctiveAction.account.displayName || event.correctiveAction.account.email}`;
                    break;
                case ALERT_EVENT_TYPES.COMMENT:
                    event.typeFormatted = `Comment by ${event.comment.account.displayName || event.comment.account.email}`;
                    break;
                case ALERT_EVENT_TYPES.RESOLVED:
                    event.typeFormatted = 'Resolved';
                    break;
                case ALERT_EVENT_TYPES.ARCHIVED:
                    if (event.archived.description === 'Archived by user') {
                        event.typeFormatted = `Archived by ${event.archived.account.displayName || event.archived.account.email}`;
                    } else {
                        event.typeFormatted = event.archived.description;
                    }
                    break;
                case 'DELAY':
                    event.typeFormatted = `Delay period: ${this.getDurationFromSeconds(trigger.triggerDelay)}`;
                    break;
                default:
                    event.typeFormatted = event.type;
            }
        })

    }

    showEventPopover(eventElement, alertEvent) {

        if (alertEvent.type !== ALERT_EVENT_TYPES.NOTIFICATION_DELIVERY && alertEvent.type !== 'DELAY') {
            return
        }

        this.popoverEvent = alertEvent

        if (alertEvent.type === ALERT_EVENT_TYPES.NOTIFICATION_DELIVERY) {
            if (alertEvent.notificationDelivery.action.type === 'EMAIL') {
                this.popoverHeader = 'The following email was sent:';
                this.popoverTitle = `${alertEvent.notificationDelivery.action.email.recipients[0]}`;
                this.popoverSubject = alertEvent.notificationDelivery.action.email.subject;
                this.popoverContent = alertEvent.notificationDelivery.action.email.body;
            }
            if (alertEvent.notificationDelivery.action.type === 'SMS') {
                this.popoverHeader = 'The following SMS was sent:';
                this.popoverTitle = `${alertEvent.notificationDelivery.action.sms.recipients[0]}`;
                this.popoverSubject = '';
                this.popoverContent = alertEvent.notificationDelivery.action.sms.body;
            }
        }

        const popover = document.getElementById(this.alert.events[0].eventId); // Need to have a unique ID for each alert, therefore using the first event ID
        const elementTarget = eventElement.currentTarget;

        popover.classList.remove('hidden');

        // Wait for the popover to render before calculating position
        setTimeout(() => {
            const containerRect = elementTarget.offsetParent.getBoundingClientRect();
            const eventRect = elementTarget.getBoundingClientRect();

            // Now that the content is rendered, clientHeight should be accurate
            const topPosition = eventRect.top - containerRect.top - popover.clientHeight / 2 + 16;

            if (!isMobileLayout()) {
                popover.style.top = `${topPosition}px`;
                popover.style.left = `312px`;
            } else {
                popover.style.top = `${eventRect.top - containerRect.top + 60}px`
                popover.style.left = `-8px`
            }
        }, 0);

    }

    getDuration(deviceTriggerTime, endTime = moment()) {
        const duration = moment.duration(endTime.diff(moment(deviceTriggerTime)))

        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        // Not using moment.js .humanize() because is rounds heavily
        const parts = []; // Build the duration string parts

        if (days > 0) {
            parts.push(`${days} days`);
        }
        if (hours > 0) {
            if (hours === 1) {
                parts.push(`${hours} hour`);
            } else {
                parts.push(`${hours} hours`);
            }
        }
        if (minutes > 0 && days === 0) { // Only show minutes if less than a day
            parts.push(`${minutes} min`);
        }

        // If there are no parts, then the duration is less than a minute
        if (parts.length === 0) {
            if (minutes === 0) {
                return 'under a minute';
            }
            return `${minutes} min`;
        }

        return parts.join(' ');
    }

    getDurationFromSeconds(secondsString) {
        // Extract the numeric value (seconds) from the string (eg. "3600s" -> 3600)
        const seconds = parseInt(secondsString, 10);

        // Create a moment.duration object
        const duration = moment.duration(seconds, 'seconds');

        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        // Not using moment.js .humanize() because is rounds too much
        // Build the duration string parts
        const parts = [];

        if (days > 0) {
            parts.push(`${days} days`);
        }
        if (hours > 0) {
            if (hours === 1) {
                parts.push(`${hours} hour`);
            } else {
                parts.push(`${hours} hours`);
            }
        }
        if (minutes > 0 && days === 0) { // Only show minutes if less than a day
            parts.push(`${minutes} min`);
        }

        // If there are no parts, then the duration is less than a minute
        if (parts.length === 0) {
            return '<1 min';
        }

        return parts.join(' ');
    }

    hoverOnEventPopover() {
        const popover = document.getElementById(this.alert.events[0].eventId); // Need to have a unique ID for each alert, therefore using the first event ID
        popover.classList.remove('hidden');
    }

    hideEventPopover() {
        const popover = document.getElementById(this.alert.events[0].eventId); // Need to have a unique ID for each alert, therefore using the first event ID
        popover.classList.add('hidden');
    }

    setRange(range) {
        this.alert.currentRange = range;
        this.alert.showRangeDropdown = false;
    }

    resetZoomToWeek() {
        this.alert.zoomedIntoAlert = false;

        // This is a workaround to reset the range of the graph. AbstractChartController will listen for the change and update the graph.
        // If we just set the range to RANGE_LABEL_WEEK, the end date will be from the current zoomed in graph, not the current date.
        this.setRange('RESET_RANGE_LAST_7_DAYS');

        setTimeout(() => {
            this.setRange(RANGE_LABEL_WEEK);
        }, 1);
    }

    goToAlertRule() {
        if (this.alert.rule.includes('organizations')) {
            this.$state.go(States.ALERTS_RULES_DETAIL, {
                projectId: typeof this.alert.project === 'object' ? this.alert.project.id : this.alert.project.split('/')[1],
                ruleId: this.alert.rule.split('/')[3]
            });
            return;
        }
        this.$state.go(States.ALERTS_RULES_DETAIL, {
            projectId: this.alert.rule.split('/')[1],
            ruleId: this.alert.rule.split('/')[3]
        });
    }

    goToDevice(deviceName) {
        this.$state.go(States.SENSOR_DETAILS, {
            projectId: deviceName.split('/')[1],
            sensorId: deviceName.split('/')[3]
        });
    }

    alertHasCorrectiveAction() {
        return this.alert.events.some(event => event.type === ALERT_EVENT_TYPES.CORRECTIVE_ACTION);
    }

    syncTagsFromAlertResponse(alertResponse) {
        if (alertResponse && Array.isArray(alertResponse.tags)) {
            this.alert.tags = alertResponse.tags;
        }
    }


    startUserAction(action) {
        this.currentUserAction = action
    }

    saveCurrentAction() {
        if (this.currentUserAction === ALERT_EVENT_TYPES.ACKNOWLEDGED) {
            this.acknowledgeAlert()
        }

        if (this.currentUserAction === ALERT_EVENT_TYPES.CORRECTIVE_ACTION && this.currentUserActionText !== '') {
            this.addCorrectiveAction()
        }

        if (this.currentUserAction === ALERT_EVENT_TYPES.COMMENT && this.currentUserActionText !== '') {
            this.addComment()
        }
    }

    acknowledgeAlert() {
        this.AlertsAndRulesService.acknowledgeAlert(this.alert, this.currentUserActionText)
            .then(alert => {

                // Unable to just replace the alert object because it will break the reference for the graph
                this.alert.events = alert.events
                this.alert.hasBeenAcknowledged = alert.hasBeenAcknowledged
                this.alert.hasCorrectiveAction = alert.hasCorrectiveAction
                this.alert.unacknowledgesAtTime = alert.unacknowledgesAtTime
                this.alert.escalatesAtTime = alert.escalatesAtTime
                this.alert.currentEscalationLevel = alert.currentEscalationLevel
                this.alert.updateTime = alert.updateTime
                this.alert.status = alert.status
                this.syncTagsFromAlertResponse(alert)
                this.convertEventsForTimeline()
                this.cancelUserAction()

                this.$scope.$applyAsync()
            });
    }

    timeToUnacknowledged() {

        // Not using humanize() because it will round up to the nearest hour
        const unackTime = moment(this.alert.unacknowledgesAtTime);
        const now = moment();
        const duration = moment.duration(unackTime.diff(now));

        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();

        const parts = [];
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(' ') : 'less than a minute';
    }

    timeToEscalation() {
        const escalationTime = moment(this.alert.escalatesAtTime);
        const now = moment();
        const duration = moment.duration(escalationTime.diff(now));

        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();

        const parts = [];
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(' ') : 'less than a minute';
    }


    addComment() {
        this.AlertsAndRulesService.addComment(this.alert, this.currentUserActionText)
            .then(alert => {
                this.alert.events = alert.events
                this.syncTagsFromAlertResponse(alert)
                this.convertEventsForTimeline()
                this.cancelUserAction()

                this.$scope.$applyAsync()
            });
    }

    addCorrectiveAction() {
        this.AlertsAndRulesService.addCorrectiveAction(this.alert, this.currentUserActionText)
            .then(alert => {

                // Unable to just replace the alert object because it will break the reference for the graph
                this.alert.events = alert.events
                this.alert.hasCorrectiveAction = alert.hasCorrectiveAction
                this.alert.unacknowledgesAtTime = alert.unacknowledgesAtTime
                this.alert.escalatesAtTime = alert.escalatesAtTime
                this.alert.currentEscalationLevel = alert.currentEscalationLevel
                this.alert.updateTime = alert.updateTime
                this.alert.status = alert.status
                this.syncTagsFromAlertResponse(alert)

                this.convertEventsForTimeline()
                this.cancelUserAction()

                this.$scope.$applyAsync()
            });
    }

    cancelUserAction() {
        this.currentUserAction = null
        this.currentUserActionText = ''
    }

    archiveAlert() {
        this.AlertsAndRulesService.archiveAlert(this.alert)
            .then(() => {
                this.$state.reload()
            });
    }

    showPauseAlertsForSensorModal() {
        this.DialogService.show({
            controller: DisableAlertsModalController,
            controllerAs: '$ctrl',
            template: DisableAlertsModalTemplate,
            parent: document.body,
            clickOutsideToClose: true,
            fullscreen: true,
            locals: {
                device: null, // Not used in alert context
                alert: this.alert,
                onSuccess: null // No callback needed in alert context
            }
        });
    }

    toggleTagPicker() {
        if (!this.canPerformAlertActions) {
            return;
        }
        this.showTagPicker = !this.showTagPicker;
        if (this.showTagPicker) {
            this.tagInput = '';
        }
    }

    closeTagPicker() {
        this.showTagPicker = false;
        this.tagInput = '';
    }

    hasTag(tag) {
        const key = (tag || '').toLowerCase();
        return (this.alert.tags || []).some(t => t.toLowerCase() === key);
    }

    togglePresetTag(tag) {
        if (this.tagSaving) return;
        const current = [...(this.alert.tags || [])];
        if (this.hasTag(tag)) {
            const filtered = current.filter(t => t.toLowerCase() !== tag.toLowerCase());
            this.persistTags(filtered);
        } else {
            this.persistTags([...current, tag]);
        }
    }

    addCustomTag() {
        const value = (this.tagInput || '').trim();
        if (!value || this.tagSaving) return;
        if (this.hasTag(value)) {
            this.tagInput = '';
            return;
        }
        const next = [...(this.alert.tags || []), value];
        this.tagInput = '';
        this.persistTags(next);
    }

    removeTag(tag) {
        if (this.tagSaving) return;
        const filtered = (this.alert.tags || []).filter(t => t.toLowerCase() !== tag.toLowerCase());
        this.persistTags(filtered);
    }

    persistTags(tags) {
        this.tagSaving = true;
        this.alert.tags = tags;
        this.AlertsAndRulesService.updateAlertTags(this.alert, tags)
            .then(updatedAlert => {
                this.syncTagsFromAlertResponse(updatedAlert);
                this.$scope.$applyAsync();
            })
            .catch(() => {})
            .finally(() => {
                this.tagSaving = false;
                this.$scope.$applyAsync();
            });
    }

    get filteredPresetTags() {
        const q = (this.tagInput || '').toLowerCase();
        const presetKeys = new Set(this.predefinedTagPresets.map(t => t.toLowerCase()));
        // Custom tags that aren't in the predefined list — show them first
        const customTags = (this.alert.tags || []).filter(t => !presetKeys.has(t.toLowerCase()));
        const allTags = [...customTags, ...this.predefinedTagPresets];
        if (!q) return allTags;
        return allTags.filter(t => t.toLowerCase().includes(q));
    }

    closeModal() {
        this.DialogService.cancel();
    }

    hasFeedback() {
        if (!this.alert || !this.alert.name) {
            return false;
        }
        const feedbackKey = `alert_feedback_${this.alert.name}`;
        return this.StorageService.getItem(feedbackKey) !== null;
    }

    getFeedback() {
        if (!this.alert || !this.alert.name) {
            return null;
        }
        const feedbackKey = `alert_feedback_${this.alert.name}`;
        return this.StorageService.getItem(feedbackKey);
    }

    submitFeedback(isHelpful) {
        if (!this.alert || !this.alert.name) {
            return;
        }

        // Show text input for both helpful and not helpful
        this.feedbackAwaitingReason = true;
        this.feedbackReasonText = '';
        this.feedbackIsHelpful = isHelpful;
    }

    submitFeedbackWithReason() {
        if (!this.alert || !this.alert.name) {
            return;
        }

        const feedbackKey = `alert_feedback_${this.alert.name}`;
        const feedback = {
            alertId: this.alert.name,
            helpful: this.feedbackIsHelpful,
            reason: this.feedbackReasonText,
            timestamp: new Date().toISOString()
        };

        this.StorageService.setItem(feedbackKey, feedback);

        // Track analytics event with full reason appended
        const baseEventName = this.feedbackIsHelpful ? 'alert_helpful_yes' : 'alert_helpful_no';
        const eventName = this.feedbackReasonText
            ? `${baseEventName}.${this.feedbackReasonText}`
            : `${baseEventName}.no_reason`;

        this.AnalyticsService.trackEvent(eventName);

        // Reset state
        this.feedbackAwaitingReason = false;
        this.feedbackReasonText = '';
        this.feedbackIsHelpful = null;
        this.feedbackJustSubmitted = true;
    }

    cancelFeedback() {
        this.feedbackAwaitingReason = false;
        this.feedbackReasonText = '';
        this.feedbackIsHelpful = null;
    }

    $onDestroy() {
    }

}
